/*-
 * #%L
 * Aires Éducatives
 * %%
 * Copyright (C) 2020 - 2023 OFB
 * %%
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 * #L%
 */

/**
 * Plugin de débogage pour améliorer l'affichage des erreurs dans la console
 * Ajoute des informations contextuelles pour faciliter l'identification des composants
 */

// Fonction pour améliorer l'affichage des erreurs globales
export function setupGlobalErrorHandling(): void {
    // Intercepter les erreurs non gérées
    window.addEventListener("error", (event) => {
        console.group("🚨 [Debug] Erreur JavaScript non gérée")
        console.error("Message:", event.message)
        console.error("Fichier:", event.filename)
        console.error("Ligne:", event.lineno)
        console.error("Colonne:", event.colno)
        console.error("Erreur complète:", event.error)

        // Tentative d'identification du composant Vue
        if (event.error && event.error.stack) {
            const vueComponentMatch = event.error.stack.match(/at (\w+)\.vue/g)
            if (vueComponentMatch) {
                console.info("🎯 [Debug] Composants Vue détectés dans la stack:", vueComponentMatch)
            }
        }

        console.groupEnd()
    })

    // Intercepter les promesses rejetées
    window.addEventListener("unhandledrejection", (event) => {
        console.group("🚨 [Debug] Promesse rejetée non gérée")
        console.error("Raison:", event.reason)
        console.error("Promesse:", event.promise)

        if (event.reason && event.reason.stack) {
            console.error("Stack trace:", event.reason.stack)

            // Tentative d'identification du composant Vue
            const vueComponentMatch = event.reason.stack.match(/at (\w+)\.vue/g)
            if (vueComponentMatch) {
                console.info("🎯 [Debug] Composants Vue détectés:", vueComponentMatch)
            }
        }

        console.groupEnd()
    })

    // Override console.error pour ajouter plus d'informations
    const originalConsoleError = console.error
    console.error = function (...args) {
        // Ajouter un timestamp et un trace
        const timestamp = new Date().toISOString()
        const stack = new Error().stack

        originalConsoleError.apply(console, [`[${timestamp}] 🚨`, ...args])

        // Afficher la stack trace si elle contient des informations utiles
        if (
            stack &&
            (stack.includes(".vue") || stack.includes("i18n") || stack.includes("component"))
        ) {
            originalConsoleError.apply(console, ["🔍 [Debug] Stack trace contextuelle:", stack])
        }
    }

    // Override console.warn de la même manière
    const originalConsoleWarn = console.warn
    console.warn = function (...args) {
        const timestamp = new Date().toISOString()
        originalConsoleWarn.apply(console, [`[${timestamp}] ⚠️`, ...args])
    }
}

// Fonction pour améliorer le débogage Vue spécifiquement
export function setupVueErrorHandling(app: any): void {
    // Configuration du gestionnaire d'erreur Vue
    app.config.errorHandler = (err: Error, vm: any, info: string) => {
        console.group("🚨 [Vue] Erreur dans un composant")
        console.error("Erreur:", err)
        console.error("Info contexte:", info)

        if (vm) {
            console.info("🎯 [Vue] Composant concerné:", {
                nom: vm.$options.name || vm.__name || "Composant anonyme",
                tag: vm.$options._componentTag,
                fichier: vm.$options.__file,
                props: vm.$props,
                data: vm.$data,
            })

            // Afficher la hiérarchie des composants parents
            let parent = vm.$parent
            const hierarchy = []
            while (parent) {
                hierarchy.push(parent.$options.name || parent.__name || "Composant anonyme")
                parent = parent.$parent
            }

            if (hierarchy.length > 0) {
                console.info("📋 [Vue] Hiérarchie des composants:", hierarchy.reverse().join(" > "))
            }
        }

        console.error("Stack trace complète:", err.stack)
        console.groupEnd()
    }

    // Configuration des warnings Vue
    app.config.warnHandler = (msg: string, vm: any, trace: string) => {
        if (process.env.NODE_ENV === "development") {
            console.group("⚠️ [Vue] Avertissement")
            console.warn("Message:", msg)

            if (vm) {
                console.info("🎯 [Vue] Composant:", vm.$options.name || vm.__name || "Composant anonyme")
            }

            if (trace) {
                console.info("🔍 [Vue] Trace:", trace)
            }

            console.groupEnd()
        }
    }
}

// Fonction utilitaire pour logger les informations de composant
export function logComponentInfo(componentName: string, context?: any): void {
    if (process.env.NODE_ENV === "development") {
        console.info(`🔧 [Debug] ${componentName}`, context || "")
    }
}
